import * as React from 'react';
import type {
  AdapterCapabilities,
  PacingOption
} from 'shared/src/domain/ai/skills/skill-platform-types';
import { Loader2 } from 'lucide-react';
import { reconcileBudgetToPrice } from 'shared/src/domain/ai/skills/budget-integrity';
import type { TypeaheadItem } from '@/components/forms/ui/AsyncTypeahead';

export type MediaPlanCardSelection = {
  /** Platform account (customer/advertiser) ID — required when capabilities.requiresAccountId. */
  accountId?: string;
  managerAccountId?: string;
  /** Meta Page the ad sets run as — required when capabilities.requiresPageId. */
  pageId?: string;
  /** Declared special ad categories (capabilities.audience.specialAdCategories). */
  specialAdCategories?: string[];
  /** Minimum audience age (capabilities.audience.defaultAgeMin seeds it). */
  ageMin?: number;
  campaignMode: 'create' | 'existing';
  campaignId?: string;
  lineItemType?: string;
  budgetType?: string;
  biddingStrategy?: string;
  /** Target CPA in dollars — required when biddingStrategy is TARGET_CPA. */
  targetCpa?: number;
  /** Target ROAS as a ratio (e.g. 3.0) — required when biddingStrategy is TARGET_ROAS. */
  targetRoas?: number;
  pacing?: PacingOption;
  cpmBid?: number;
  /** Optional Max CPC ceiling in dollars — only meaningful with the MAXIMIZE_CLICKS strategy. */
  maxCpc?: number;
  /** Optional tracking / UTM string pasted from the plan (becomes a template or URL suffix). */
  tracking?: string;
  geo?: string[];
  /**
   * Optional multi-campaign split (platforms with capabilities.supportsCampaignSplit, default off).
   * When present with >1 entry, the line item runs as multiple campaigns whose budget shares must
   * sum to the line-item price; otherwise it is a single campaign (the fields above).
   */
  campaigns?: CampaignGroupSelection[];
};

/** One campaign in a multi-campaign split. */
export type CampaignGroupSelection = {
  name?: string;
  /** Objective-split platforms: this row's objective (one of capabilities.lineItemTypes). */
  objective?: string;
  /** Objective-split platforms: the row's performance goal, when its objective offers any. */
  performanceGoal?: string;
  biddingStrategy?: string;
  targetCpa?: number;
  targetRoas?: number;
  /** This campaign's lifetime dollar share of the line-item price. */
  budgetShare?: number;
  geo?: string;
};

export type MediaPlanInferredFromZoho = {
  lineItemType?: boolean;
  geo?: boolean;
};

type Props = {
  capabilities: AdapterCapabilities;
  platformName: string;
  selection: MediaPlanCardSelection;
  isBusy: boolean;
  inferredFromZoho?: MediaPlanInferredFromZoho;
  onChange: (next: MediaPlanCardSelection) => void;
  onFinalize: () => void;
  hasFinalized: boolean;
  /** Line-item price, for the campaign-split budget reconciliation. */
  lineItemPrice?: number;
  /** Searches DSP + campaign-linked accounts for the account picker (requiresAccountId only). */
  onSearchAccounts?: (search: string) => Promise<TypeaheadItem[]>;
  /** Searches the DSP's Pages for the Page picker (requiresPageId only); absent = paste the id. */
  onSearchPages?: (search: string) => Promise<TypeaheadItem[]>;
};

export function MediaPlanConfigurationCard({
  capabilities,
  platformName,
  selection,
  isBusy,
  inferredFromZoho,
  onChange,
  onFinalize,
  hasFinalized,
  lineItemPrice,
  onSearchAccounts,
  onSearchPages
}: Props) {
  const status = getStatus(selection, isBusy, capabilities);
  const accountLabel = capabilities.accountLabel ?? 'account';
  // An objective split is create-only and carries the objective per row, so the single-campaign
  // sections (mode, line-item type, bidding, tracking) have nothing to configure.
  const objectiveSplit = capabilities.objectiveSplit;

  function patch(p: Partial<MediaPlanCardSelection>) {
    onChange({ ...selection, ...p });
  }

  return (
    <div
      className={`rounded-xl border p-4 shadow-sm transition-colors ${
        status.type === 'awaiting'
          ? 'border-amber-300 bg-amber-50/30'
          : status.type === 'populating'
            ? 'border-blue-300 bg-blue-50/30'
            : 'border-gray-200 bg-white'
      }`}>
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-gray-900">
            {platformName} Media Plan Configuration
          </h3>
          <PlatformPill label={platformName} />
          <StatusBadge status={status} />
        </div>
      </div>
      <p className="-mt-2 mb-3 text-[11px] text-gray-500">
        Fields below are specific to the {platformName} DSP.
      </p>

      {capabilities.managerAccounts && capabilities.managerAccounts.length > 1 ? (
        <Section title={`${platformName} Manager Account`}>
          <SelectField
            label="Manager account (MCC)"
            value={selection.managerAccountId}
            options={capabilities.managerAccounts.map(m => ({ id: m.id, name: m.label }))}
            onChange={v => patch({ managerAccountId: v || undefined, accountId: undefined })}
          />
          <div className="text-xs text-gray-500">
            Scopes the {accountLabel} search below to this manager account. Changing it clears the
            pinned {accountLabel}.
          </div>
        </Section>
      ) : null}

      {capabilities.requiresAccountId ? (
        <Section title={`${platformName} ${capitalize(accountLabel)}`}>
          {onSearchAccounts ? (
            <AccountIdSearchField
              label={`${platformName} ${accountLabel} ID`}
              value={selection.accountId ?? ''}
              placeholder={capabilities.accountIdPlaceholder ?? 'Search by name or enter an ID'}
              searchFn={onSearchAccounts}
              onChange={v => patch({ accountId: v || undefined })}
            />
          ) : (
            <TextField
              label={`${platformName} ${accountLabel} ID`}
              value={selection.accountId ?? ''}
              placeholder={capabilities.accountIdPlaceholder ?? 'e.g. 123-456-7890'}
              onChange={v => patch({ accountId: v || undefined })}
            />
          )}
          <div className="text-xs text-gray-500">
            The {platformName} {accountLabel} the campaign is created under.
            {onSearchAccounts ? ' Search by name, or enter the ID directly.' : ''} Verified when the
            preview is generated.
          </div>
        </Section>
      ) : null}

      {capabilities.requiresPageId ? (
        <Section title={`${platformName} Page`}>
          {onSearchPages ? (
            <AccountIdSearchField
              label={`${platformName} Page ID`}
              value={selection.pageId ?? ''}
              placeholder="Search by name or enter a Page ID"
              searchFn={onSearchPages}
              onChange={v => patch({ pageId: v || undefined })}
            />
          ) : (
            <TextField
              label={`${platformName} Page ID`}
              value={selection.pageId ?? ''}
              placeholder="e.g. 123456789012345"
              onChange={v => patch({ pageId: v || undefined })}
            />
          )}
          <div className="text-xs text-gray-500">
            The Page the ads run as. Must be owned by or shared with the business portfolio;
            verified when the preview is generated.
          </div>
        </Section>
      ) : null}

      <Section title={`${platformName} Campaign`} hidden={Boolean(objectiveSplit)}>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-500">Mode</label>
          <div className="flex gap-2">
            <ModeButton
              active={selection.campaignMode === 'create'}
              onClick={() => patch({ campaignMode: 'create', campaignId: undefined })}
              label={`Create new ${platformName} campaign`}
            />
            <ModeButton
              active={selection.campaignMode === 'existing'}
              onClick={() => patch({ campaignMode: 'existing' })}
              label={`Use existing ${platformName} campaign`}
            />
          </div>
        </div>
        {selection.campaignMode === 'existing' ? (
          <TextField
            label={`Existing ${platformName} campaign ID`}
            value={selection.campaignId ?? ''}
            placeholder="e.g. 12345"
            onChange={v => patch({ campaignId: v || undefined })}
          />
        ) : null}
      </Section>

      <Section title={`${platformName} Line Item`} hidden={Boolean(objectiveSplit)}>
        <SelectField
          label="Type"
          value={selection.lineItemType}
          options={capabilities.lineItemTypes.map(t => ({ id: t, name: t }))}
          onChange={v => patch({ lineItemType: v || undefined })}
          inferredFromZoho={inferredFromZoho?.lineItemType}
        />
        <SelectField
          label="Budget type"
          value={selection.budgetType}
          options={capabilities.budgetTypes.map(t => ({ id: t, name: t }))}
          onChange={v => patch({ budgetType: v || undefined })}
        />
      </Section>

      <Section title={`${platformName} Bidding`} hidden={Boolean(objectiveSplit)}>
        <SelectField
          label="Strategy"
          value={selection.biddingStrategy}
          options={capabilities.biddingStrategies.map(s => ({ id: s, name: s }))}
          onChange={v =>
            patch({
              biddingStrategy: v || undefined,
              // Clear the other strategy's target so a stale value can't be forwarded.
              targetCpa: v === 'TARGET_CPA' ? selection.targetCpa : undefined,
              targetRoas: v === 'TARGET_ROAS' ? selection.targetRoas : undefined
            })
          }
        />
        {selection.biddingStrategy === 'TARGET_CPA' ? (
          <>
            <NumberField
              label="Target CPA ($)"
              value={selection.targetCpa}
              onChange={v => patch({ targetCpa: v })}
              placeholder="e.g. 25.00"
            />
            <div className="text-xs text-gray-500">
              Requires conversion tracking on the Google Ads account, or the campaign create will be
              rejected.
            </div>
          </>
        ) : null}
        {selection.biddingStrategy === 'TARGET_ROAS' ? (
          <>
            <NumberField
              label="Target ROAS (ratio)"
              value={selection.targetRoas}
              onChange={v => patch({ targetRoas: v })}
              placeholder="e.g. 3.0 = 300%"
            />
            <div className="text-xs text-gray-500">
              Ratio, not dollars (3.0 = 300%). Requires conversion tracking with values on the
              Google Ads account, or the campaign create will be rejected.
            </div>
          </>
        ) : null}
        {capabilities.pacingOptions && capabilities.pacingOptions.length > 0 ? (
          <SelectField
            label="Pacing"
            value={selection.pacing}
            options={capabilities.pacingOptions.map(p => ({ id: p, name: p }))}
            onChange={v => patch({ pacing: (v as PacingOption) || undefined })}
          />
        ) : null}
        {selection.biddingStrategy === 'MAXIMIZE_CLICKS' ? (
          <>
            <NumberField
              label="Max Unit Cost (CPC) ($)"
              value={selection.maxCpc}
              onChange={v => patch({ maxCpc: v })}
              placeholder="optional — leave blank to bid freely"
            />
            <div className="text-xs text-gray-500">
              Optional. Caps the highest cost-per-click. Leave blank unless the client is
              CPC-sensitive — a ceiling can restrict learning.
            </div>
          </>
        ) : null}
        {capabilities.usesCpmBid ? (
          <NumberField
            label="CPM bid"
            value={selection.cpmBid}
            onChange={v => patch({ cpmBid: v })}
            placeholder="e.g. 8.50"
          />
        ) : null}
      </Section>

      <Section title={`${platformName} Tracking`} hidden={Boolean(objectiveSplit)}>
        <TextField
          label="Tracking / UTM (optional)"
          value={selection.tracking ?? ''}
          placeholder="paste tracking template or ?utm_source=…"
          onChange={v => patch({ tracking: v || undefined })}
        />
        <div className="text-xs text-gray-500">
          Optional. A value containing <code>{'{lpurl}'}</code> is sent as a tracking URL template;
          anything else is treated as a final-URL query-string suffix.
        </div>
      </Section>

      <Section title={`${platformName} Targeting`}>
        <GeoField
          value={selection.geo ?? []}
          onChange={v => patch({ geo: v })}
          inferredFromZoho={inferredFromZoho?.geo}
        />
        {capabilities.targetingModules.length > 0 ? (
          <div className="text-xs text-gray-500">
            Additional targeting modules ({capabilities.targetingModules.length}) will be derived
            from Zoho on finalize.
          </div>
        ) : null}
      </Section>

      {capabilities.audience ? (
        <Section title={`${platformName} Audience`}>
          {capabilities.audience.specialAdCategories?.length ? (
            <>
              <CheckboxGroupField
                label="Special ad categories"
                options={capabilities.audience.specialAdCategories}
                value={selection.specialAdCategories ?? []}
                onChange={v => patch({ specialAdCategories: v.length > 0 ? v : undefined })}
              />
              <div className="text-xs text-gray-500">
                Declare only when the client is in a regulated vertical. Declaring one drops the
                minimum age below — {platformName} enforces its own floor.
              </div>
            </>
          ) : null}
          {capabilities.audience.defaultAgeMin !== undefined ? (
            <NumberField
              label="Minimum age"
              value={selection.ageMin}
              onChange={v => patch({ ageMin: v })}
              placeholder={`default ${capabilities.audience.defaultAgeMin}`}
              step="1"
              min={String(capabilities.audience.defaultAgeMin)}
              max={
                capabilities.audience.maxAgeMin !== undefined
                  ? String(capabilities.audience.maxAgeMin)
                  : undefined
              }
            />
          ) : null}
        </Section>
      ) : null}

      {objectiveSplit ? (
        <ObjectiveSplitSection
          platformName={platformName}
          objectives={capabilities.lineItemTypes}
          performanceGoals={objectiveSplit.performanceGoals ?? {}}
          campaigns={selection.campaigns ?? []}
          lineItemPrice={lineItemPrice}
          onChange={campaigns => patch({ campaigns })}
        />
      ) : capabilities.supportsCampaignSplit && selection.campaignMode === 'create' ? (
        <CampaignSplitSection
          platformName={platformName}
          capabilities={capabilities}
          campaigns={selection.campaigns}
          lineItemPrice={lineItemPrice}
          onChange={campaigns => patch({ campaigns })}
        />
      ) : null}

      <FinalizeRow
        platformName={platformName}
        status={status}
        hasFinalized={hasFinalized}
        onFinalize={onFinalize}
      />
    </div>
  );
}

/** The card's own readiness gate, shared with the finalize handler so the button never looks Ready
 * yet no-ops (or the reverse). */
export function isSelectionComplete(
  s: MediaPlanCardSelection,
  capabilities: AdapterCapabilities
): boolean {
  return getStatus(s, false, capabilities).type === 'complete';
}

/** Default rows for an objective-split platform: one per objective, shares as a percent of the
 * line-item price with the last row absorbing rounding so the split reconciles exactly. */
export function seedObjectiveSplitRows(
  capabilities: AdapterCapabilities,
  lineItemPrice: number | undefined
): CampaignGroupSelection[] | undefined {
  const split = capabilities.objectiveSplit;
  if (!split) return undefined;
  const rows = split.defaults.map(d => ({
    objective: d.objective,
    performanceGoal: split.performanceGoals?.[d.objective]?.[0],
    budgetShare:
      lineItemPrice === undefined ? undefined : Math.round(lineItemPrice * d.sharePercent) / 100
  }));
  if (lineItemPrice !== undefined && rows.length > 0) {
    const allButLast = rows.slice(0, -1).reduce((sum, r) => sum + (r.budgetShare ?? 0), 0);
    rows[rows.length - 1].budgetShare = Math.round((lineItemPrice - allButLast) * 100) / 100;
  }
  return rows;
}

/** Budget shares reconcile to the line-item price — uses the shared budget-integrity primitive so
 * the card and the server agree on the tolerance. */
export function splitReconciles(
  campaigns: CampaignGroupSelection[],
  lineItemPrice?: number
): boolean {
  if (lineItemPrice === undefined) return true;
  const total = campaigns.reduce((sum, g) => sum + (g.budgetShare ?? 0), 0);
  return reconcileBudgetToPrice({ lineItemPrice, campaignBudgetsTotal: total }).ok;
}

/** Geo input → clean list. Entries are separated by `;` or a newline, never a comma, so a
 * "City, Region" pair ("Springfield, Illinois") reaches the platform intact — the resolvers use
 * the region qualifier to disambiguate, and a comma split would target the region on its own. */
export function parseGeoText(raw: string): string[] {
  return raw
    .split(/[;\n]/)
    .map(s => s.trim())
    .filter(s => s.length > 0);
}

function CampaignSplitSection({
  platformName,
  capabilities,
  campaigns,
  lineItemPrice,
  onChange
}: {
  platformName: string;
  capabilities: AdapterCapabilities;
  campaigns?: CampaignGroupSelection[];
  lineItemPrice?: number;
  onChange: (campaigns: CampaignGroupSelection[] | undefined) => void;
}) {
  const groups = campaigns ?? [];
  const newGroup = (): CampaignGroupSelection => ({
    biddingStrategy: capabilities.defaultBiddingStrategy,
    budgetShare: 0
  });

  if (groups.length === 0) {
    return (
      <Section title={`${platformName} Campaign Split (optional)`}>
        <div className="text-xs text-gray-500">
          Runs as one campaign by default. Split into multiple campaigns (e.g. brand vs non-brand,
          or per-geo) only when each needs its own bidding, geo, or budget. Each campaign becomes
          its own media plan; the shares must sum to the line-item price.
        </div>
        <button
          type="button"
          className="mt-1 rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
          onClick={() =>
            onChange([{ ...newGroup(), budgetShare: lineItemPrice ?? 0 }, newGroup()])
          }>
          + Split into multiple campaigns
        </button>
      </Section>
    );
  }

  const total = groups.reduce((sum, g) => sum + (g.budgetShare ?? 0), 0);
  const reconciles = splitReconciles(groups, lineItemPrice);
  const patchGroup = (i: number, p: Partial<CampaignGroupSelection>) =>
    onChange(groups.map((g, idx) => (idx === i ? { ...g, ...p } : g)));
  const removeGroup = (i: number) => {
    const next = groups.filter((_, idx) => idx !== i);
    onChange(next.length <= 1 ? undefined : next);
  };

  return (
    <Section title={`${platformName} Campaign Split`}>
      {groups.map((g, i) => (
        <div key={`campaign-${i}`} className="rounded-md border border-gray-200 p-2">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-600">Campaign {i + 1}</span>
            <button
              type="button"
              className="text-xs text-red-500 hover:underline"
              onClick={() => removeGroup(i)}>
              Remove
            </button>
          </div>
          <TextField
            label="Name (optional)"
            value={g.name ?? ''}
            placeholder="e.g. Brand"
            onChange={v => patchGroup(i, { name: v || undefined })}
          />
          <SelectField
            label="Bidding"
            value={g.biddingStrategy}
            options={capabilities.biddingStrategies.map(s => ({ id: s, name: s }))}
            onChange={v =>
              patchGroup(i, {
                biddingStrategy: v || undefined,
                targetCpa: v === 'TARGET_CPA' ? g.targetCpa : undefined,
                targetRoas: v === 'TARGET_ROAS' ? g.targetRoas : undefined
              })
            }
          />
          {g.biddingStrategy === 'TARGET_CPA' ? (
            <NumberField
              label="Target CPA ($)"
              value={g.targetCpa}
              onChange={v => patchGroup(i, { targetCpa: v })}
              placeholder="e.g. 25.00"
            />
          ) : null}
          {g.biddingStrategy === 'TARGET_ROAS' ? (
            <NumberField
              label="Target ROAS (ratio)"
              value={g.targetRoas}
              onChange={v => patchGroup(i, { targetRoas: v })}
              placeholder="e.g. 3.0"
            />
          ) : null}
          <NumberField
            label="Budget share ($)"
            value={g.budgetShare}
            onChange={v => patchGroup(i, { budgetShare: v })}
            placeholder="lifetime $ for this campaign"
          />
          <TextField
            label="Geo (optional)"
            value={g.geo ?? ''}
            placeholder="e.g. California"
            onChange={v => patchGroup(i, { geo: v || undefined })}
          />
        </div>
      ))}
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
          onClick={() => onChange([...groups, newGroup()])}>
          + Add campaign
        </button>
        <button
          type="button"
          className="rounded px-2 py-1 text-xs text-gray-500 hover:underline"
          onClick={() => onChange(undefined)}>
          Remove split (single campaign)
        </button>
      </div>
      <div className={`text-xs ${reconciles ? 'text-gray-500' : 'text-red-500'}`}>
        Budget total: ${total.toFixed(2)}
        {lineItemPrice !== undefined ? ` / $${lineItemPrice.toFixed(2)} line-item price` : ''}
        {reconciles ? '' : ' — shares must sum to the line-item price'}
      </div>
    </Section>
  );
}

function ObjectiveSplitSection({
  platformName,
  objectives,
  performanceGoals,
  campaigns,
  lineItemPrice,
  onChange
}: {
  platformName: string;
  objectives: string[];
  performanceGoals: Record<string, string[]>;
  campaigns: CampaignGroupSelection[];
  lineItemPrice?: number;
  onChange: (campaigns: CampaignGroupSelection[]) => void;
}) {
  const total = campaigns.reduce((sum, g) => sum + (g.budgetShare ?? 0), 0);
  const reconciles = splitReconciles(campaigns, lineItemPrice);
  const patchGroup = (i: number, p: Partial<CampaignGroupSelection>) =>
    onChange(campaigns.map((g, idx) => (idx === i ? { ...g, ...p } : g)));
  const newGroup = (): CampaignGroupSelection => ({
    objective: objectives[0],
    performanceGoal: performanceGoals[objectives[0]]?.[0],
    budgetShare: 0
  });

  return (
    <Section title={`${platformName} Campaigns`}>
      <div className="text-xs text-gray-500">
        One campaign (+ one ad set) per row, each its own media plan. Shares must sum to the
        line-item price.
      </div>
      {campaigns.map((g, i) => {
        const goals = g.objective ? (performanceGoals[g.objective] ?? []) : [];
        return (
          <div key={`campaign-${i}`} className="rounded-md border border-gray-200 p-2">
            <div className="mb-1 flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-600">Campaign {i + 1}</span>
              {campaigns.length > 1 ? (
                <button
                  type="button"
                  className="text-xs text-red-500 hover:underline"
                  onClick={() => onChange(campaigns.filter((_, idx) => idx !== i))}>
                  Remove
                </button>
              ) : null}
            </div>
            <SelectField
              label="Objective"
              value={g.objective}
              options={objectives.map(o => ({ id: o, name: o }))}
              onChange={v =>
                patchGroup(i, {
                  objective: v || undefined,
                  performanceGoal: performanceGoals[v]?.[0]
                })
              }
            />
            {goals.length > 0 ? (
              <SelectField
                label="Performance goal"
                value={g.performanceGoal}
                options={goals.map(goal => ({ id: goal, name: goal }))}
                onChange={v => patchGroup(i, { performanceGoal: v || undefined })}
              />
            ) : null}
            <NumberField
              label="Budget share ($)"
              value={g.budgetShare}
              onChange={v => patchGroup(i, { budgetShare: v })}
              placeholder="lifetime $ for this campaign"
            />
            <TextField
              label="Name suffix (optional)"
              value={g.name ?? ''}
              placeholder="e.g. Retargeting — only when two rows share an objective"
              onChange={v => patchGroup(i, { name: v || undefined })}
            />
          </div>
        );
      })}
      <button
        type="button"
        className="rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
        onClick={() => onChange([...campaigns, newGroup()])}>
        + Add campaign
      </button>
      <div className={`text-xs ${reconciles ? 'text-gray-500' : 'text-red-500'}`}>
        Budget total: ${total.toFixed(2)}
        {lineItemPrice !== undefined ? ` / $${lineItemPrice.toFixed(2)} line-item price` : ''}
        {reconciles ? '' : ' — shares must sum to the line-item price'}
      </div>
    </Section>
  );
}

function FinalizeRow({
  platformName,
  status,
  hasFinalized,
  onFinalize
}: {
  platformName: string;
  status: ConfigStatus;
  hasFinalized: boolean;
  onFinalize: () => void;
}) {
  const ready = status.type === 'complete';
  const label = hasFinalized
    ? `${platformName} preview generated`
    : ready
      ? `Preview ${platformName} media plan`
      : status.label;
  return (
    <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-3">
      <span className="text-[11px] text-gray-500">
        {hasFinalized
          ? `Review the preview card; nothing is created in ${platformName} until you confirm there.`
          : ready
            ? `Generates a preview card. Nothing is created in ${platformName} until you confirm there.`
            : 'Fill in remaining fields to enable preview.'}
      </span>
      <button
        type="button"
        onClick={onFinalize}
        disabled={!ready}
        className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
          ready
            ? 'bg-blue-600 text-white hover:bg-blue-700'
            : 'cursor-not-allowed bg-gray-100 text-gray-400'
        }`}>
        {label}
      </button>
    </div>
  );
}

type ConfigStatus =
  | { type: 'populating'; label: string }
  | { type: 'awaiting'; label: string }
  | { type: 'complete'; label: string };

function getStatus(
  s: MediaPlanCardSelection,
  isBusy: boolean,
  capabilities: AdapterCapabilities
): ConfigStatus {
  if (isBusy) return { type: 'populating', label: 'Working...' };
  if (capabilities.requiresAccountId && !s.accountId)
    return { type: 'awaiting', label: `Enter the ${capabilities.accountLabel ?? 'account'} ID` };
  if (capabilities.requiresPageId && !s.pageId)
    return { type: 'awaiting', label: 'Enter the Page ID' };
  // The HTML min/max/step only hint; the server rejects a fractional or out-of-range age at confirm.
  const ageFloor = capabilities.audience?.defaultAgeMin;
  const ageCeiling = capabilities.audience?.maxAgeMin;
  if (
    ageFloor !== undefined &&
    s.ageMin !== undefined &&
    (!Number.isInteger(s.ageMin) || s.ageMin < ageFloor)
  )
    return { type: 'awaiting', label: `Minimum age must be a whole number, ${ageFloor} or more` };
  if (ageCeiling !== undefined && s.ageMin !== undefined && s.ageMin > ageCeiling)
    return { type: 'awaiting', label: `Minimum age must be ${ageCeiling} or less` };
  if (capabilities.objectiveSplit) {
    const rows = s.campaigns ?? [];
    if (rows.length === 0) return { type: 'awaiting', label: 'Add at least one campaign' };
    if (rows.some(c => !c.objective))
      return { type: 'awaiting', label: 'Pick an objective for each campaign' };
    if (rows.some(c => c.budgetShare === undefined || c.budgetShare <= 0))
      return { type: 'awaiting', label: 'Set a budget share for each campaign' };
    return { type: 'complete', label: 'Ready' };
  }
  if (s.campaignMode === 'existing' && !s.campaignId)
    return { type: 'awaiting', label: 'Enter campaign ID' };
  if (!s.lineItemType) return { type: 'awaiting', label: 'Pick a line item type' };
  if (!s.budgetType) return { type: 'awaiting', label: 'Pick a budget type' };
  if (!s.biddingStrategy) return { type: 'awaiting', label: 'Pick a bidding strategy' };
  if (s.biddingStrategy === 'TARGET_CPA' && (s.targetCpa === undefined || s.targetCpa <= 0))
    return { type: 'awaiting', label: 'Set a target CPA' };
  if (s.biddingStrategy === 'TARGET_ROAS' && (s.targetRoas === undefined || s.targetRoas <= 0))
    return { type: 'awaiting', label: 'Set a target ROAS' };
  if (capabilities.pacingOptions && capabilities.pacingOptions.length > 0 && !s.pacing)
    return { type: 'awaiting', label: 'Pick a pacing option' };
  if (capabilities.usesCpmBid && (s.cpmBid === undefined || s.cpmBid <= 0))
    return { type: 'awaiting', label: 'Set a CPM bid' };
  // Multi-campaign split: each campaign needs a bidding strategy + positive budget share to build a
  // valid proposal (hard gates). A budget-split mismatch is NOT a hard gate — per Design Principle 1
  // it surfaces as an overridable warning in the split section (and the server re-emits it as a
  // notice), so the user can still proceed.
  if (s.campaigns && s.campaigns.length > 1) {
    if (s.campaigns.some(c => !c.biddingStrategy))
      return { type: 'awaiting', label: 'Pick a bidding strategy for each campaign' };
    if (s.campaigns.some(c => c.budgetShare === undefined || c.budgetShare <= 0))
      return { type: 'awaiting', label: 'Set a budget share for each campaign' };
  }
  return { type: 'complete', label: 'Ready' };
}

function PlatformPill({ label }: { label: string }) {
  return (
    <span
      title={`Settings on this card configure the ${label} DSP`}
      className="inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-indigo-700">
      {label} DSP
    </span>
  );
}

function StatusBadge({ status }: { status: ConfigStatus }) {
  if (status.type === 'populating') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-medium text-blue-700">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-blue-500" />
        {status.label}
      </span>
    );
  }
  if (status.type === 'awaiting') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
        {status.label}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-medium text-green-700">
      <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
      {status.label}
    </span>
  );
}

function Section({
  title,
  hidden,
  children
}: {
  title: string;
  hidden?: boolean;
  children: React.ReactNode;
}) {
  if (hidden) return null;
  return (
    <div className="mb-4 last:mb-0">
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
        {title}
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function ZohoInferredBadge() {
  return (
    <span
      title="Inferred from Zoho — click to change"
      className="inline-flex items-center rounded bg-purple-100 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-purple-700">
      from Zoho
    </span>
  );
}

function ModeButton({
  active,
  onClick,
  label
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md border px-3 py-1 text-xs ${
        active
          ? 'border-blue-400 bg-blue-50 text-blue-800'
          : 'border-gray-300 bg-white text-gray-800 hover:border-gray-400'
      }`}>
      {label}
    </button>
  );
}

function SelectField({
  label,
  value,
  options,
  onChange,
  inferredFromZoho
}: {
  label: string;
  value?: string;
  options: Array<{ id: string; name: string }>;
  onChange: (v: string) => void;
  inferredFromZoho?: boolean;
}) {
  return (
    <div>
      <div className="mb-1 flex items-center gap-2">
        <label className="block text-xs font-medium text-gray-500">{label}</label>
        {inferredFromZoho ? <ZohoInferredBadge /> : null}
      </div>
      <select
        value={value ?? ''}
        onChange={e => onChange(e.target.value)}
        className="h-9 w-full rounded-md border border-gray-300 bg-white px-2 text-sm leading-normal text-gray-900">
        <option value="" disabled>
          Select {label.toLowerCase()}...
        </option>
        {options.map(opt => (
          <option key={opt.id} value={opt.id}>
            {opt.name}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * Combobox for the account id: one input that is both the pinned id value and the search box.
 * Focusing lists accounts immediately (empty search returns everything); typing filters
 * server-side AND is itself the id — picking from the list just fills in the exact id, so a
 * pasted id that matches nothing is still usable as entered.
 */
function AccountIdSearchField({
  label,
  value,
  placeholder,
  searchFn,
  onChange
}: {
  label: string;
  value: string;
  placeholder?: string;
  searchFn: (search: string) => Promise<TypeaheadItem[]>;
  onChange: (v: string) => void;
}) {
  const [results, setResults] = React.useState<TypeaheadItem[]>([]);
  const [isOpen, setIsOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [selectedLabel, setSelectedLabel] = React.useState<string | null>(null);
  const [errorText, setErrorText] = React.useState<string | null>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const searchFnRef = React.useRef(searchFn);
  searchFnRef.current = searchFn;
  // Stale-response guard: only the latest request may update the list.
  const requestSeq = React.useRef(0);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const runSearch = React.useCallback((search: string) => {
    const seq = ++requestSeq.current;
    setIsLoading(true);
    searchFnRef
      .current(search)
      .then(items => {
        if (seq !== requestSeq.current) return;
        setResults(items);
        setErrorText(null);
        setIsOpen(true);
      })
      .catch((err: unknown) => {
        if (seq !== requestSeq.current) return;
        setResults([]);
        setErrorText(err instanceof Error ? err.message : 'Search failed');
        setIsOpen(true);
      })
      .finally(() => {
        if (seq === requestSeq.current) setIsLoading(false);
      });
  }, []);

  React.useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  function handleInput(v: string) {
    onChange(v);
    setSelectedLabel(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(v), 300);
  }

  function handleSelect(item: TypeaheadItem) {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    requestSeq.current++;
    onChange(item.id);
    setSelectedLabel(item.label);
    setIsOpen(false);
  }

  return (
    <div ref={containerRef} className="relative">
      <label className="mb-1 block text-xs font-medium text-gray-500">{label}</label>
      <div className="relative">
        <input
          type="text"
          value={value}
          placeholder={placeholder}
          onChange={e => handleInput(e.target.value)}
          onFocus={() => runSearch(value)}
          onKeyDown={e => {
            if (e.key === 'Escape') setIsOpen(false);
          }}
          className="h-9 w-full rounded-md border border-gray-300 bg-white px-2 text-sm leading-normal text-gray-900"
        />
        {isLoading ? (
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <Loader2 className="size-4 animate-spin text-gray-400" />
          </div>
        ) : null}
      </div>
      {selectedLabel ? <div className="mt-1 text-xs text-gray-500">{selectedLabel}</div> : null}
      {isOpen ? (
        <div className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-gray-200 bg-white shadow-md">
          {errorText ? (
            <div className="px-3 py-2 text-sm text-red-600">Lookup failed: {errorText}</div>
          ) : results.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">
              No matches — the entered value is used as the ID.
            </div>
          ) : (
            results.map(item => (
              <button
                key={item.id}
                type="button"
                className="w-full cursor-default px-3 py-1.5 text-left text-sm text-gray-900 hover:bg-gray-100"
                onClick={() => handleSelect(item)}>
                {item.label}
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

function TextField({
  label,
  value,
  placeholder,
  onChange
}: {
  label: string;
  value: string;
  placeholder?: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-gray-500">{label}</label>
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        className="h-9 w-full rounded-md border border-gray-300 bg-white px-2 text-sm leading-normal text-gray-900"
      />
    </div>
  );
}

function NumberField({
  label,
  value,
  placeholder,
  onChange,
  step = '0.01',
  min = '0',
  max
}: {
  label: string;
  value?: number;
  placeholder?: string;
  onChange: (v: number | undefined) => void;
  step?: string;
  min?: string;
  max?: string;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-gray-500">{label}</label>
      <input
        type="number"
        step={step}
        min={min}
        max={max}
        value={value ?? ''}
        placeholder={placeholder}
        onChange={e => {
          const n = e.target.value === '' ? undefined : Number(e.target.value);
          onChange(Number.isFinite(n) ? (n as number) : undefined);
        }}
        className="h-9 w-full rounded-md border border-gray-300 bg-white px-2 text-sm leading-normal text-gray-900"
      />
    </div>
  );
}

function CheckboxGroupField({
  label,
  options,
  value,
  onChange
}: {
  label: string;
  options: string[];
  value: string[];
  onChange: (v: string[]) => void;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-gray-500">{label}</label>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {options.map(opt => {
          const checked = value.includes(opt);
          return (
            <label key={opt} className="flex items-center gap-1.5 text-xs text-gray-800">
              <input
                type="checkbox"
                checked={checked}
                onChange={() => onChange(checked ? value.filter(v => v !== opt) : [...value, opt])}
              />
              {opt}
            </label>
          );
        })}
      </div>
    </div>
  );
}

function GeoField({
  value,
  onChange,
  inferredFromZoho
}: {
  value: string[];
  onChange: (v: string[]) => void;
  inferredFromZoho?: boolean;
}) {
  // Local raw text is the source of truth while typing, so spaces and trailing commas survive
  // keystrokes. Parsing to the trimmed array (for the parent) happens on change, but the input
  // never re-renders from the parsed value mid-typing — otherwise typing a space inside a name
  // ("New York") trims it away before the next character lands.
  const [text, setText] = React.useState(() => joinGeo(value));

  // Re-seed local text only when the parent value changes from outside this input (e.g. Zoho
  // inference prefill). Comparing against the parsed local text means our own keystrokes — whose
  // in-progress spaces parse to the same array — don't clobber what's being typed.
  React.useEffect(() => {
    if (joinGeo(value) !== joinGeo(parseGeoText(text))) {
      setText(joinGeo(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <div>
      <div className="mb-1 flex items-center gap-2">
        <label className="block text-xs font-medium text-gray-500">Geo</label>
        {inferredFromZoho ? <ZohoInferredBadge /> : null}
      </div>
      <input
        type="text"
        value={text}
        placeholder="e.g. United States; Springfield, Illinois"
        onChange={e => {
          setText(e.target.value);
          onChange(parseGeoText(e.target.value));
        }}
        className="h-9 w-full rounded-md border border-gray-300 bg-white px-2 text-sm leading-normal text-gray-900"
      />
      <div className="mt-1 text-xs text-gray-500">
        Separate locations with a semicolon; keep a city with its state (e.g. Springfield,
        Illinois).
      </div>
    </div>
  );
}

function joinGeo(entries: string[]): string {
  return entries.join('; ');
}

function capitalize(s: string): string {
  return s.length === 0 ? s : s.charAt(0).toUpperCase() + s.slice(1);
}
